/**
 * The ONE axios client for the API's PUBLIC surface (`/api/v1/public/*`).
 *
 * These routes take no auth — the token in the URL is the authorization — so
 * this client is deliberately Firebase-free:
 *
 *   - No auth interceptors fire (no Firebase ID token, no App Check).
 *   - The bundle that renders a public page can omit Firebase entirely.
 *   - `withCredentials: false`: a public page must never smuggle the
 *     `.letsorderup.com` session cookie at a route that would ignore it.
 *
 * It still sends the Vercel deployment-protection bypass, or a public page on a
 * preview/staging alias receives the HTML auth wall instead of the API and
 * reports it as a product failure.
 *
 * ── Why it is shared ────────────────────────────────────────────────────────
 * This was `createPublicClient()`, private to `publicPaymentApi.ts`, when it
 * had one caller. The guest table page (Guest QR Platform, ruling R2) is the
 * second, and a second COPY of a client whose job is "which headers a page with
 * no identity may send" is the copied-helper drift class: both keep working
 * while they stop agreeing, and the disagreement is a security posture. So the
 * construction moved here verbatim and `publicPaymentApi` now calls it.
 *
 * ── The thrown error, and why `.status` rides along ─────────────────────────
 * Errors come back as `{ success: false, error: { code, message } }`; the
 * message is unwrapped into a thrown `Error` for the React Query layer. Both
 * `.code` and `.status` are stamped on it:
 *
 *   - `.code` lets `translateError` / `ERROR_CATALOG` resolve actionable copy.
 *   - `.status` is what `translateError`'s `readStatus` reads. Stamping neither
 *     made its ENTIRE status-aware fallback (401/403, 400/409/422, 429, >=500)
 *     unreachable from this client, so every uncatalogued refusal collapsed to
 *     the same generic "Something went wrong. Please try again."
 *
 * `.status` is also how a caller tells a MISS from a failure on a route whose
 * miss body is deliberately not an envelope — the public tables routes answer
 * a bare `{"state":"unknown"}` 404 so they cannot be used as an existence
 * oracle, and there is nothing in that body to branch on.
 */

import axios from "axios";
import type { ApiClient } from "./apiClient";
import { foldLocationResponseNames } from "@order-up/common";

import { getApiUrl, getAutomationBypassSecret } from "../utils/env";
import {
  DEPLOYMENT_PROTECTED_CODE,
  isDeploymentProtectionResponse,
} from "../utils/deploymentProtection";

/** The envelope every public route answers with (misses excepted — see above). */
export interface PublicEnvelope<T> {
  success: boolean;
  data?: T;
  error?: { code?: string; message?: string };
}

/** An error thrown by the public client: the server's code and HTTP status. */
export interface PublicApiError extends Error {
  code?: string;
  status?: number;
}

/**
 * Typed as {@link ApiClient} for the same reason `createApiClient` is: the
 * interceptor below resolves every call with the BODY, so axios's
 * `AxiosResponse<T>` typing was one level wrong.
 */
export function createPublicApiClient(): ApiClient {
  const apiUrl = getApiUrl();
  const baseURL = apiUrl
    ? `${apiUrl}/api/v1/public`
    : `http://localhost:3002/api/v1/public`;

  const client = axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
      ...(getAutomationBypassSecret() && {
        "x-vercel-protection-bypass": getAutomationBypassSecret(),
      }),
    },
    withCredentials: false,
  });

  client.interceptors.response.use(
    // Location rename (Phase 3, Task 26): fold the new wire names back
    // onto the legacy ones every caller reads (see apiClient.ts).
    (response) => foldLocationResponseNames(response.data),
    (error) => {
      if (axios.isAxiosError(error) && error.response) {
        // A non-JSON (HTML) 401/403 is the deployment-protection wall, not our
        // envelope — surface the stable code so the page shows the right copy
        // instead of a misleading product error.
        if (isDeploymentProtectionResponse(error)) {
          const wall: PublicApiError = new Error(
            `${DEPLOYMENT_PROTECTED_CODE}: The service is behind deployment protection and could not be reached.`,
          );
          wall.code = DEPLOYMENT_PROTECTED_CODE;
          return Promise.reject(wall);
        }
        const data = error.response.data as PublicEnvelope<unknown> | undefined;
        const message = data?.error?.message ?? error.message;
        const typed: PublicApiError = new Error(message);
        if (data?.error?.code) typed.code = data.error.code;
        if (typeof error.response.status === "number") {
          typed.status = error.response.status;
        }
        return Promise.reject(typed);
      }
      return Promise.reject(error);
    },
  );

  return client as unknown as ApiClient;
}

/**
 * Unwrap `{ success, data }`, throwing the server's message otherwise.
 *
 * `data === undefined` is the failure — `data === null` is a legitimate answer
 * on routes whose payload is nullable, and must not be mistaken for one.
 */
export async function unwrapPublicEnvelope<T>(
  promise: Promise<unknown>,
): Promise<T> {
  const envelope = (await promise) as PublicEnvelope<T>;
  if (!envelope || !envelope.success || envelope.data === undefined) {
    throw new Error(envelope?.error?.message ?? "Request failed.");
  }
  return envelope.data;
}
